import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import CambioClaveObligatorio from './components/CambioClaveObligatorio';
import { rutaPanel, esComprador, esAdminPlataforma } from './utils/rutasAcceso';

// Layouts
import MainLayout from './components/layout/MainLayout';
import ClienteLayout from './components/layout/ClienteLayout';
import PujaLayout from './components/layout/PujaLayout';

// Páginas panel cliente (rol=3)
import ClienteDashboard from './pages/Cliente/ClienteDashboard';
import MisAnimales from './pages/Cliente/MisAnimales';
import Remates from './pages/Cliente/Remates';
import MisOfertas from './pages/Cliente/MisOfertas';
import MisSeguimientos from './pages/Cliente/MisSeguimientos';
import Tickets from './pages/Cliente/Tickets';
import Notificaciones from './pages/Cliente/Notificaciones';

// Páginas públicas
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import RematePage from './pages/Micrositio/RematePage';
import AnimalPage from './pages/Micrositio/AnimalPage';
import CatalogoPage from './pages/Micrositio/CatalogoPage';
import AnimalCatalogoPage from './pages/Micrositio/AnimalCatalogoPage';
import SeccionCatalogoPage from './pages/Micrositio/SeccionCatalogoPage';
import CondicionesPage from './pages/Micrositio/CondicionesPage';
import ContactoPage from './pages/Micrositio/ContactoPage';
import EnVivoPage from './pages/Micrositio/EnVivoPage';

// Páginas privadas
import Dashboard from './pages/Dashboard/Dashboard';
import AnimalesList from './pages/Animales/AnimalesList';
import AnimalDetalle from './pages/Animales/AnimalDetalle';
import AnimalForm from './pages/Animales/AnimalForm';
import CargaMasiva from './pages/Animales/CargaMasiva';
import Familiares from './pages/Animales/Familiares';
import VincularFamiliares from './pages/Animales/VincularFamiliares';
import FotosVideos from './pages/Animales/FotosVideos';
import EventosList from './pages/Eventos/EventosList';
import Invitaciones from './pages/Invitaciones/Invitaciones';
import EventoDetalle from './pages/Eventos/EventoDetalle';
import EventoForm from './pages/Eventos/EventoForm';
import MisClientes from './pages/Clientes/MisClientes';
import ClientesList from './pages/Clientes/ClientesList';
import Cabanas from './pages/Cabanas/Cabanas';
import Micrositio from './pages/Herramientas/Micrositio';
import GenerarPDF from './pages/Herramientas/GenerarPDF';
import CatalogoGenetica from './pages/Herramientas/CatalogoGenetica';
import ConsultasCatalogo from './pages/Herramientas/ConsultasCatalogo';
import Analytics from './pages/Analytics/Analytics';
import AnalyticsCompleto from './pages/Analytics/AnalyticsCompleto';
import MiPerfil from './pages/MiPerfil/MiPerfil';
import VentaAnimales from './pages/Ventas/VentaAnimales';
import TableroVentas from './pages/Ventas/TableroVentas';
import Movimientos from './pages/Ventas/Movimientos';
import MovimientoDetalle from './pages/Ventas/MovimientoDetalle';
import Categorias from './pages/Categorias/Categorias';
import TicketsAdmin from './pages/Consultas/TicketsAdmin';
import PujaTickets from './pages/PujaAdmin/Tickets';

// Guard exclusivo para compradores (rol=3)
const ClienteRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Cargando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!esComprador(user)) return <Navigate to={rutaPanel(user)} replace />;
  return children;
};

// Guard exclusivo para Puja admin (rol=1, sin cabaña)
const PujaRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Cargando...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!esAdminPlataforma(user)) return <Navigate to={rutaPanel(user)} replace />;
  return children;
};

// El catch-all necesita leer el usuario, y eso solo se puede dentro de un
// componente. Sin login va al login; con login, al panel de SU rol.
const RedirigirASuPanel = () => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Cargando...</div>;
  return <Navigate to={isAuthenticated ? rutaPanel(user) : '/login'} replace />;
};

// Componente para rutas protegidas
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return <div>Cargando...</div>;
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Componente para rutas públicas (redirige si ya está logueado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <div>Cargando...</div>;
  if (!isAuthenticated) return children;
  return <Navigate to={rutaPanel(user)} replace />;
};

// Componente para rutas que requieren un recurso específico
const ProtectedRoute = ({ children, recurso }) => {
  const { tieneRecurso, loading } = useAuth();
  
  if (loading) {
    return <div>Cargando...</div>;
  }
  
  // Si no tiene el recurso, redirigir al dashboard
  if (recurso && !tieneRecurso(recurso)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <>
      {/* Fuera de <Routes> a propósito: la clave temporal hay que cambiarla
          esté donde esté el usuario, y también cuando entra por una sesión
          restaurada sin pasar por el login. El componente se auto-oculta si el
          usuario no tiene el flag.

          Efecto buscado, no accidente: esto también tapa el micrositio PÚBLICO
          si el comprador está logueado con una clave blanqueada. Se deja así
          porque igual no puede ofertar hasta cambiarla, y dejarlo navegar
          primero sólo le corre el trámite para el momento en que quiere pujar
          — que es justo cuando no quiere trámites. */}
      <CambioClaveObligatorio />
    <Routes>
      {/* Rutas públicas */}
      <Route path="/login" element={
        <PublicRoute>
          <Login />
        </PublicRoute>
      } />
      <Route path="/registro" element={
        <PublicRoute>
          <Register />
        </PublicRoute>
      } />
      
      {/* Rutas privadas con layout */}
      <Route path="/" element={
        <PrivateRoute>
          <MainLayout />
        </PrivateRoute>
      }>
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        
        {/* Animales */}
        <Route path="animales" element={<AnimalesList />} />
        <Route path="animales/nuevo" element={<AnimalForm />} />
        <Route path="animales/carga-masiva" element={<CargaMasiva />} />
        <Route path="animales/familiares" element={<Familiares />} />
        <Route path="animales/vincular-familiares" element={<VincularFamiliares />} />
        <Route path="animales/fotos-videos" element={<FotosVideos />} />
        <Route path="animales/:id" element={<AnimalDetalle />} />
        <Route path="animales/:id/editar" element={<AnimalForm />} />
        
        {/* Eventos */}
        <Route path="eventos" element={<EventosList />} />
        <Route path="invitaciones" element={<Invitaciones />} />
        {/* La MISMA pantalla de carga masiva, en modo remate: la columna
            CABANA del CSV pasa a decidir el dueño de cada animal. */}
        <Route path="eventos/:id/carga-masiva" element={<CargaMasiva />} />
        <Route path="eventos/nuevo" element={<EventoForm />} />
        <Route path="eventos/:id" element={<EventoDetalle />} />
        <Route path="eventos/:id/editar" element={<EventoForm />} />
        
        {/* Clientes */}
        <Route path="clientes" element={<MisClientes />} />
        <Route path="clientes/lista" element={<ClientesList />} />
        <Route path="clientes/nuevo" element={<Navigate to="/clientes" replace />} />
        <Route path="clientes/:id" element={<Navigate to="/clientes" replace />} />
        <Route path="clientes/:id/editar" element={<Navigate to="/clientes" replace />} />
        
        {/* Cabañas — `ver_cabanas`, el MISMO recurso con el que la gatea el
            menu (MainLayout) y con el que la pide el backend en las cinco
            rutas de `cabana_routes.py`. Estaba con `multi_cabanas`, que es
            otro concepto y de otro modulo: organizar un remate con varias
            cabañas. Un plan con `ver_cabanas` y sin `multi_cabanas` mostraba
            "Cabañas" en el menu y al hacer click rebotaba al dashboard SIN
            ningun mensaje — un teletransporte silencioso, que es peor que el
            403 legible del que se queja el comentario del propio menu. */}
        <Route path="cabanas" element={
          <ProtectedRoute recurso="ver_cabanas">
            <Cabanas />
          </ProtectedRoute>
        } />
        
        {/* Herramientas */}
        {/* Las tres con el MISMO recurso que las gatea en el menu. Micrositio
            y Generar PDF no tenian `ProtectedRoute`: el menu las escondia pero
            un bookmark o una URL tipeada entraban igual. El backend sigue
            siendo la guarda real, pero tres comportamientos distintos para la
            misma pregunta son dos de mas. */}
        <Route path="herramientas/micrositio" element={
          <ProtectedRoute recurso="config_identidad">
            <Micrositio />
          </ProtectedRoute>
        } />
        <Route path="herramientas/generar-pdf" element={
          <ProtectedRoute recurso="generar_pdf">
            <GenerarPDF />
          </ProtectedRoute>
        } />
        <Route path="herramientas/catalogo-genetica" element={
          <ProtectedRoute recurso="catalogo_genetica">
            <CatalogoGenetica />
          </ProtectedRoute>
        } />
        {/* Mismo recurso que el catálogo: es su bandeja de entrada, no una
            herramienta aparte que el plan pueda vender por separado. */}
        <Route path="herramientas/consultas-catalogo" element={
          <ProtectedRoute recurso="catalogo_genetica">
            <ConsultasCatalogo />
          </ProtectedRoute>
        } />

        {/* Mi Perfil */}
        <Route path="mi-perfil" element={<MiPerfil />} />

        {/* Ventas de Animales */}
        <Route path="ventas/animales" element={<VentaAnimales />} />
        <Route path="ventas/tablero" element={<TableroVentas />} />
        <Route path="ventas/movimientos" element={<Movimientos />} />
        <Route path="ventas/movimientos/:id_animal" element={<MovimientoDetalle />} />

        {/* Categorías */}
        <Route path="categorias" element={<Categorias />} />

        {/* Consultas y Reclamos */}
        <Route path="consultas" element={<TicketsAdmin />} />

        {/* Notificaciones */}
        <Route path="notificaciones" element={<Notificaciones />} />

        {/* Analytics */}
        <Route path="analytics" element={
          <ProtectedRoute recurso="analytics">
            <Analytics />
          </ProtectedRoute>
        } />
        <Route path="analytics/completo/:tipo" element={
          <ProtectedRoute recurso="analytics">
            <AnalyticsCompleto />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Panel cliente (rol=3) */}
      <Route path="/cliente" element={
        <ClienteRoute>
          <ClienteLayout />
        </ClienteRoute>
      }>
        <Route index element={<Navigate to="/cliente/dashboard" replace />} />
        <Route path="dashboard"    element={<ClienteDashboard />} />
        <Route path="mis-animales" element={<MisAnimales />} />
        <Route path="remates"      element={<Remates />} />
        <Route path="mis-ofertas"  element={<MisOfertas />} />
        <Route path="seguimiento"  element={<MisSeguimientos />} />
        <Route path="tickets"      element={<Tickets />} />
        <Route path="notificaciones" element={<Notificaciones />} />
        {/* Mismo componente que usa el productor (solo pega a /auth/perfil y
            /auth/cambiar-password, agnosticos al rol), pero montado DENTRO del
            bloque /cliente. Apuntar al /mi-perfil del productor sacaba al
            comprador de su propio panel: PrivateRoute solo mira que este
            logueado, no el rol, asi que caia en el MainLayout de la cabaña. */}
        <Route path="mi-perfil"    element={<MiPerfil />} />
      </Route>

      {/* Panel Puja admin (rol=1, sin cabaña) */}
      <Route path="/puja" element={
        <PujaRoute>
          <PujaLayout />
        </PujaRoute>
      }>
        <Route index element={<Navigate to="/puja/tickets" replace />} />
        <Route path="tickets" element={<PujaTickets />} />
      </Route>

      {/* Micrositio público — no requiere auth */}
      {/* El catalogo de genetica, publico. Va junto a las del remate y no
          adentro del layout autenticado: no pide login, igual que
          `/remate/:slug`. */}
      <Route path="/genetica/:slug" element={<CatalogoPage />} />
      <Route path="/genetica/:slug/animal/:animalSlug" element={<AnimalCatalogoPage />} />
      {/* El listado completo de una seccion. Va DESPUES de la del animal a
          proposito: esa tiene cuatro segmentos y el literal "animal", asi que
          no compiten, pero leerlas en este orden deja a la vista que
          `/genetica/x/toros` y `/genetica/x/animal/y` conviven en el mismo
          prefijo. Que `:seccion` sea una clave valida lo decide el backend,
          que contesta 404 a cualquier otra cosa. */}
      <Route path="/genetica/:slug/:seccion" element={<SeccionCatalogoPage />} />
      <Route path="/remate/:slug" element={<RematePage />} />
      <Route path="/remate/:slug/animal/:animalSlug" element={<AnimalPage />} />
      <Route path="/remate/:slug/condiciones-comerciales" element={<CondicionesPage tipo="comercial" />} />
      <Route path="/remate/:slug/condiciones-sanitarias" element={<CondicionesPage tipo="sanitaria" />} />
      <Route path="/remate/:slug/reclamos-garantias" element={<CondicionesPage tipo="reclamos" />} />
      <Route path="/remate/:slug/contacto" element={<ContactoPage />} />
      <Route path="/remate/:slug/en-vivo" element={<EnVivoPage />} />

      {/* Ruta 404 — al panel que le corresponde al rol, no siempre al del
          productor: un comprador con un bookmark viejo terminaba en el
          MainLayout de la cabaña. */}
      <Route path="*" element={<RedirigirASuPanel />} />
    </Routes>
    </>
  );
}

export default App;
